export interface Value {
  assets: AssetValue[],
}

export interface AssetValue {
  asset: AssetId,
  value: bigint,
}

export interface Account {
  accountId: string,
  delegatedKeys: string[],
  uncommittedValue: Value,
  nonce: bigint,
}

export interface Deposit {
  accountId: string,
  requestId: bigint,
  absorbedInBlock: bigint,
  rejectedInBlock: bigint,
  refundTx: Uint8Array | null,
  failureReason: string | null,
  value: Value,
}

/** One side of a market in the directory: the asset, its canonical id string,
 * its alias (ticker), and its decimals for rendering raw amounts. */
export interface AssetInfo {
  asset: AssetId,
  id: string,
  alias: string | null,
  decimals: number,
}

export interface Market {
  key: string,
  tradingPair: TradingPair,
  base?: AssetInfo,
  quote?: AssetInfo,
  takerFee: Ratio,
  makerFee: Ratio,
}

export interface OrderbookDepth {
  key: string,
  pairKey: string,
  pair: TradingPair,
  price: bigint,
  bidAmount: bigint,
  askAmount: bigint,
}

export interface Order {
  id: bigint,
  owner: string,
  pair: TradingPair,
  side: Side,
  price: bigint,
  originalAmount: bigint,
  remaining: bigint,
  status: OrderStatus,
  created: bigint,
  destination: Destination,
  lastExecuted: bigint,
}

export interface Candlestick {
  key: string,
  pairKey: string,
  granularity: string,
  /** Bucket start, unix MILLISECONDS. */
  timestamp: bigint,
  open: bigint,
  high: bigint,
  low: bigint,
  close: bigint,
  /** The OHLC prices pre-rendered as human decimal strings. */
  openDecimal?: string,
  highDecimal?: string,
  lowDecimal?: string,
  closeDecimal?: string,
  volume: bigint,
  tradeCount: number,
}

export interface TpsStats {
  id: number,
  totalEvents: bigint,
  lastEventAt: bigint,
  currentSecondCount: number,
  currentSecond: bigint,
  lastSecondCount: number,
}

export interface TradingPair {
  base: AssetId,
  quote: AssetId,
}

export type AssetId = AdaAssetId | NativeTokenAssetId

export interface AdaAssetId {
  tag: "Ada",
}

export interface NativeTokenAssetId {
  tag: "NativeToken",
  policyId: string,
  assetName: string,
}

export interface Ratio {
  numerator: number,
  denominator: number,
}

export type Side = { tag: "Bid" } | { tag: "Ask" }

export type OrderStatus =
  | { tag: "New" }
  | { tag: "PartiallyFilled" }
  | { tag: "Filled" }
  | { tag: "Cancelled" }

export interface Destination {
  address: string,
  datum: Datum | null,
}

export type Datum = DatumVariantInline | DatumVariantHash

export interface DatumVariantInline {
  tag: "Inline",
  inline: string,
}

export interface DatumVariantHash {
  tag: "Hash",
  hash: string,
}

export function parseAssetValue(raw: any): AssetValue {
  return { asset: raw.asset, value: BigInt(raw.value) };
}

export function parseValue(raw: any): Value {
  return { assets: (raw?.assets ?? []).map(parseAssetValue) };
}

export function adaAssetId(): AdaAssetId {
  return { tag: "Ada" };
}

export function parseNativeTokenAssetId(raw: any): NativeTokenAssetId {
  return { tag: "NativeToken", policyId: raw.policyId, assetName: raw.assetName };
}

export function parseAssetId(raw: any): AssetId {
  return raw.tag === "Ada" ? adaAssetId() : parseNativeTokenAssetId(raw);
}

export function parseTradingPair(raw: any): TradingPair {
  return { base: parseAssetId(raw.base), quote: parseAssetId(raw.quote) };
}

export function parseRatio(raw: any): Ratio {
  return { numerator: raw.numerator, denominator: raw.denominator };
}

export function parseSide(raw: any): Side {
  return { tag: raw };
}

export function parseOrderStatus(raw: any): OrderStatus {
  return { tag: raw };
}

export function parseDatumVariantInline(raw: any): DatumVariantInline {
  return { tag: "Inline", inline: raw.inline };
}

export function parseDatumVariantHash(raw: any): DatumVariantHash {
  return { tag: "Hash", hash: raw.hash };
}

export function parseDatum(raw: any): Datum {
  return raw.tag === "Inline" ? parseDatumVariantInline(raw) : parseDatumVariantHash(raw);
}

export function parseDestination(raw: any): Destination {
  return { address: raw.address, datum: raw.datum ? parseDatum(raw.datum) : null };
}

export function parseAccount(raw: any): Account {
  return {
    ...raw,
    delegatedKeys: raw.delegatedKeys ?? [],
    nonce: BigInt(raw.nonce),
    uncommittedValue: parseValue(raw.uncommittedValue),
  };
}

export function parseDeposit(raw: any): Deposit {
  return {
    ...raw,
    requestId: BigInt(raw.requestId),
    absorbedInBlock: BigInt(raw.absorbedInBlock),
    rejectedInBlock: BigInt(raw.rejectedInBlock),
    value: parseValue(raw.value),
  };
}

/** Per-market ticker (public `ticker:<market>` channel). Raw u128 fixed-point
 * prices, nullable when the market has never traded / has no side. */
export interface Ticker {
  market: string,
  lastPrice: bigint | null,
  bestBid: bigint | null,
  bestAsk: bigint | null,
}

export function parseTicker(raw: any): Ticker {
  const big = (v: unknown) => (v == null ? null : BigInt(v as string));
  return {
    market: String(raw.market),
    lastPrice: big(raw.lastPrice),
    bestBid: big(raw.bestBid),
    bestAsk: big(raw.bestAsk),
  };
}

export function parseAssetInfo(raw: any): AssetInfo | undefined {
  if (!raw) return undefined;
  return {
    asset: parseAssetId(raw.asset),
    id: String(raw.id),
    alias: raw.alias ?? null,
    decimals: Number(raw.decimals),
  };
}

export function parseMarket(raw: any): Market {
  return {
    key: raw.key,
    tradingPair: parseTradingPair(raw.tradingPair),
    base: parseAssetInfo(raw.base),
    quote: parseAssetInfo(raw.quote),
    takerFee: parseRatio(raw.takerFee),
    makerFee: parseRatio(raw.makerFee),
  };
}

export function parseOrderbookDepth(raw: any): OrderbookDepth {
  return {
    ...raw,
    bidAmount: BigInt(raw.bidAmount),
    askAmount: BigInt(raw.askAmount),
    price: BigInt(raw.price),
  };
}

export function parseOrder(raw: any): Order {
  return {
    ...raw,
    id: BigInt(raw.id),
    price: BigInt(raw.price),
    originalAmount: BigInt(raw.originalAmount),
    remaining: BigInt(raw.remaining),
    created: BigInt(raw.created),
    lastExecuted: BigInt(raw.lastExecuted),
    side: { tag: raw.side },
    status: { tag: raw.status },
  };
}

export function parseCandlestick(raw: any): Candlestick {
  return {
    ...raw,
    timestamp: BigInt(raw.timestamp),
    open: BigInt(raw.open),
    high: BigInt(raw.high),
    low: BigInt(raw.low),
    close: BigInt(raw.close),
    volume: BigInt(raw.volume),
  };
}

export function parseTpsStats(raw: any): TpsStats {
  return {
    ...raw,
    totalEvents: BigInt(raw.totalEvents),
    lastEventAt: BigInt(raw.lastEventAt),
    currentSecond: BigInt(raw.currentSecond),
  };
}

// --- Competition: live leaderboards and the news wire ---

export interface LeaderboardEntry {
  accountId: string,
  score: number,
  /** X handle, present only when the account opted in to public display. */
  handle?: string,
}

export interface Leaderboard {
  category: string,
  entries: LeaderboardEntry[],
  updatedAtMs: bigint,
}

export interface NewsItem {
  timeMs: bigint,
  token: string,
  announced: boolean,
  headline: string,
}

export function parseLeaderboard(raw: any): Leaderboard {
  return {
    category: raw.category,
    entries: (raw.entries ?? []).map((e: any) => ({ accountId: e.accountId, score: Number(e.score), handle: e.handle ?? undefined })),
    updatedAtMs: BigInt(raw.updatedAtMs),
  };
}

export function parseNewsItem(raw: any): NewsItem {
  return {
    timeMs: BigInt(raw.timeMs),
    token: raw.token,
    announced: !!raw.announced,
    headline: raw.headline,
  };
}
